import React from "react";

import Modal from "../Shared/Modal";
import { ButtonInput } from "../Shared/Inputs/ButtonInput";
import { CDN_URL } from "../../services/config";
import Dropzone from "react-dropzone";
import LoadingWheel from "../Shared/LoadingWheel";
import { UploadIcon } from "@heroicons/react/outline";
import useApiHelper from "../../services/useApiHelper";
import useNotification from "../notification/useNotifications";

interface ImportPeopleModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  agencyId: string;
}

const downloadCSV = (url: string, filename: string) => {
  fetch(url)
    .then(response => {
      return response.blob();
    })
    .then(blob => {
      const link = document.createElement('a');
      const objectUrl = URL.createObjectURL(blob);
      link.href = objectUrl;
      link.download = filename;
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    })
    .catch(() => {
      // Fallback: open the file URL in a new tab/window. This avoids CORS fetch errors
      // because the browser will directly request the resource and either download it
      // or render it according to the server's headers.
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });
};

export function ImportPeopleModal({ open, setOpen, agencyId }: ImportPeopleModalProps) {
  const { formData } = useApiHelper();
  const { addNotification } = useNotification();
  const [uploading, setUploading] = React.useState(false);

  const handleDownloadTemplate = () => {
    const fileUrl = `${CDN_URL}/public/EstateTrack_Import_Template.csv`
    downloadCSV(fileUrl, 'contact-import-template.csv');
  };

  const processFiles = async (acceptFiles: File[]) => {
    setUploading(true);

    acceptFiles.forEach(async (file) => {
      let fd = new FormData();
      fd.append("contacts", file, "contacts.csv");

      try {
        const res = await formData(`/person/${agencyId}/import`, fd)

        if (res.success) {
          addNotification({ variant: "success", primaryText: "Contacts uploaded successfully" });
          setOpen(false);
        }
      } catch {
        addNotification({ variant: "error", primaryText: "Error uploading contacts" });
      } finally {
        setUploading(false);
      }
    });
  };

  return (
    <Modal open={open} setOpen={setOpen} width="max-w-2xl">
      <h3 className="text-lg px-2 leading-6 font-medium text-gray-900">
        Import Contacts
      </h3>
      <div className="mt-2 p-2">
        <p>
          Please download and fill in the template file to import your contacts.
        </p>
        {/* @ts-ignore */}
        <ButtonInput
          classes="mt-2"
          isSubmit={false}
          label="Download Template"
          onClick={handleDownloadTemplate}
        />
      </div>
      <div className="mt-2 p-2 space-y-4">
        <p>Once you have a filled template, you can upload it here to import your contacts.</p>
        <Dropzone onDrop={processFiles} maxFiles={1}>
          {({ getRootProps, getInputProps }) => (
            <section>
              <div
                className="flex flex-col items-center text-gray-500 hover:text-gray-700  bg-white border-2 border-gray-300 border-dashed rounded-lg p-10 hover:border-gray-400"
                {...getRootProps()}
              >
                <input {...getInputProps()} />
                {uploading ? (
                  <LoadingWheel width="w-6" height="h-6" />
                ) : (
                  <>
                    <UploadIcon className="w-16 h-16 m-2" />
                    <span className="text-center">
                      Drop some files here, or click to select files
                    </span>
                  </>
                )}
              </div>
            </section>
          )}
        </Dropzone>
      </div>
    </Modal>
  );
}